import { openDialog } from "./dialog";
import { themeOverridesCss } from "./theme";
import triggerStyles from "./trigger.css";
import type { ConfigResponse, EmbedOptions } from "./types";

// Entry point. Runs once when the embed script loads.
//
// Reads config from the current <script> tag's data-* attrs:
//   data-site="lmbpainting"            (required)
//   data-api="https://chatbot.example" (optional; defaults to script's origin)
//   data-mount="#some-selector"        (optional; inline mode instead of floating)
//
// Flow: fetch /v1/config → if site is enabled + not rate-limited, inject a
// trigger button → on click, lazy-init the dialog module.

(function bootstrap() {
  const script = (document.currentScript ??
    findScript()) as HTMLScriptElement | null;
  if (!script) {
    console.warn("[scalebloom-chatbot] could not locate embed script tag");
    return;
  }

  const site = script.dataset.site;
  if (!site) {
    console.warn("[scalebloom-chatbot] missing data-site on script tag");
    return;
  }

  const apiBase =
    script.dataset.api ?? new URL(script.src, location.href).origin;
  const mountSel = script.dataset.mount ?? null;
  const mount = mountSel ? document.querySelector<HTMLElement>(mountSel) : null;

  const opts: EmbedOptions = { site, apiBase, mount };

  // Defer until DOM is interactive so query selectors can find the mount.
  ready(() => void start(opts));
})();

function findScript(): HTMLScriptElement | null {
  // Fallback when document.currentScript is null (async/defer in some edge cases).
  const scripts = document.querySelectorAll<HTMLScriptElement>(
    "script[data-site][src*='chatbot']",
  );
  return scripts[scripts.length - 1] ?? null;
}

function ready(fn: () => void) {
  if (document.readyState !== "loading") fn();
  else document.addEventListener("DOMContentLoaded", fn, { once: true });
}

async function start(opts: EmbedOptions) {
  let payload: ConfigResponse;
  try {
    const res = await fetch(
      `${opts.apiBase}/v1/config?site=${encodeURIComponent(opts.site)}`,
      { credentials: "omit" },
    );
    if (!res.ok) {
      console.warn(`[scalebloom-chatbot] config fetch failed: ${res.status}`);
      return;
    }
    payload = (await res.json()) as ConfigResponse;
  } catch (err) {
    console.warn("[scalebloom-chatbot] config fetch error", err);
    return;
  }

  if (!payload.status.allowed) return; // hide the button entirely
  mountTrigger(opts, payload);
}

function mountTrigger(opts: EmbedOptions, { config }: ConfigResponse) {
  const host = document.createElement("div");
  // `all: initial` would also reset font-family to the UA default (Times New
  // Roman). Re-enable inheritance for font so the button picks up the host
  // page's typography — anything inside the shadow root can `font: inherit`.
  host.style.cssText = "all: initial; font: inherit;";
  const shadow = host.attachShadow({ mode: "closed" });

  const style = document.createElement("style");
  // Theme overrides come AFTER the main stylesheet so DB-configured colors
  // win over the :host fallbacks declared in trigger.css.
  style.textContent = triggerStyles + themeOverridesCss(config.themeOverrides);
  shadow.appendChild(style);

  const btn = document.createElement("button");
  btn.type = "button";
  btn.setAttribute("aria-label", "Ask AI");
  btn.className = opts.mount ? "trigger-inline" : "trigger-floating";
  btn.innerHTML = `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M19 9l1.25-2.75L23 5l-2.75-1.25L19 1l-1.25 2.75L15 5l2.75 1.25L19 9zm-7.5.5L9 4 6.5 9.5 1 12l5.5 2.5L9 20l2.5-5.5L17 12l-5.5-2.5zM19 15l-1.25 2.75L15 19l2.75 1.25L19 23l1.25-2.75L23 19l-2.75-1.25L19 15z"
            fill="currentColor"/>
    </svg>
    <span>Ask AI</span>
  `;
  (opts.mount ?? document.body).appendChild(host);
  shadow.appendChild(btn);

  btn.addEventListener("click", () => {
    openDialog(opts, config);
  });
}
